import * as React from 'react';

const REVEAL_MS_PER_CHAR = 4;

/**
 * Steadily reveal ``target`` one character at a time (a typewriter effect)
 * while ``active`` is true, returning the substring revealed so far.
 *
 * The reveal is driven by a single ``requestAnimationFrame`` loop, so it
 * commits at most once per frame (~60 Hz) regardless of how fast SSE deltas
 * arrive -- each frame reveals however many characters ``REVEAL_MS_PER_CHAR``
 * allows for the elapsed time and does a single ``setState``. When ``active``
 * is false the full target is shown immediately with no animation.
 *
 * The per-frame render cost is kept off the hot path two ways: the consuming
 * block view is the only thing that re-renders (see ``BlockRouter`` passing
 * blocks by reference + the memoized block views), and ``StreamingMarkdown``
 * skips syntax highlighting while streaming so the growing code blocks aren't
 * re-tokenized every frame.
 *
 * :param target: The full text to reveal.
 * :param active: Whether to animate the reveal (true while streaming).
 * :return: The portion of ``target`` revealed so far.
 */
export function useSteadyRevealText(target: string, active: boolean): string {
  const [displayed, setDisplayed] = React.useState(active ? target.slice(0, 1) : target);
  const targetRef = React.useRef(target);
  const displayedRef = React.useRef(displayed);
  const rafRef = React.useRef<number | null>(null);
  const lastTickRef = React.useRef<number | null>(null);
  const carryRef = React.useRef(0);
  const tickRef = React.useRef<(now: number) => void>(() => undefined);

  displayedRef.current = displayed;

  const stop = React.useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    lastTickRef.current = null;
    carryRef.current = 0;
  }, []);

  const start = React.useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame((now) => tickRef.current(now));
  }, []);

  tickRef.current = (now: number) => {
    const targetText = targetRef.current;
    const currentText = displayedRef.current;
    const gap = targetText.length - currentText.length;
    if (gap <= 0) {
      stop();
      return;
    }

    const last = lastTickRef.current ?? now;
    lastTickRef.current = now;
    carryRef.current += now - last;

    const count = Math.min(gap, Math.floor(carryRef.current / REVEAL_MS_PER_CHAR));
    if (count > 0) {
      carryRef.current -= count * REVEAL_MS_PER_CHAR;
      const next = targetText.slice(0, currentText.length + count);
      displayedRef.current = next;
      setDisplayed(next);
    }

    rafRef.current = requestAnimationFrame((nextNow) => tickRef.current(nextNow));
  };

  React.useEffect(() => {
    targetRef.current = target;

    if (!active) {
      displayedRef.current = target;
      setDisplayed(target);
      stop();
      return;
    }

    if (target.length === 0) {
      displayedRef.current = '';
      setDisplayed('');
      stop();
      return;
    }

    if (!target.startsWith(displayedRef.current)) {
      displayedRef.current = target.slice(0, 1);
      setDisplayed(displayedRef.current);
    } else if (displayedRef.current.length === 0) {
      displayedRef.current = target.slice(0, 1);
      setDisplayed(displayedRef.current);
    }

    if (displayedRef.current.length < target.length) start();
  }, [target, active, start, stop]);

  React.useEffect(() => stop, [stop]);

  return displayed;
}
